import React, { useState } from "react";
import styled from "@emotion/styled";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { Box } from "@mui/material";
import { AccountTypeEnum, CACHE_KEYS } from "@bn/api/apiv1";
import { GET_ACCOUNTS } from "@api/cacheKeys";
import { CreateOrUpdateAccountParams } from "@api";
import { useCreateAccount } from "@api/v1/hooks/useAccounts";
import { CreateAndEditAccountForm } from "@features/accounts/edit/components/CreateAndEditAccountForm";
import { LoadingButton } from "@components";

export const StyledButton = styled(LoadingButton)`
  margin-top: ${({ theme }) => theme.spacing(6)};
  width: 100%;
`;

const HorizontalDivider = styled.div`
  margin: 32px 0 10px;
  border-bottom: 1px solid ${({ theme }) => theme.palette.border.secondary};
`;

export const AddManuallyBlock = ({
  closeModal,
  onBack,
  defaultType,
}: {
  closeModal: () => void;
  onBack: () => void;
  defaultType?: AccountTypeEnum;
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [accountValues, setAccountValues] =
    useState<CreateOrUpdateAccountParams>({
      name: "",
      type: "01_depot",
      enableImplicitTransactions: true,
    });
  const { mutateAsync, isPending } = useCreateAccount();

  const handleCreateAccount = () => {
    mutateAsync(accountValues).then(() => {
      queryClient.invalidateQueries({
        queryKey: [GET_ACCOUNTS],
      });
      queryClient.invalidateQueries({
        queryKey: [CACHE_KEYS.LIST_AND_SEARCH_ALL_ACCOUNTS],
      });
      closeModal();
    });
  };

  const handleUpdateAccountValues = (
    params: Partial<CreateOrUpdateAccountParams>
  ) => {
    setAccountValues((values) => {
      return { ...values, ...params };
    });
  };

  return (
    <Box maxWidth="700px" width="100%">
      <CreateAndEditAccountForm
        accountValues={accountValues}
        onUpdateAccountValues={handleUpdateAccountValues}
        defaultType={defaultType}
      />
      <HorizontalDivider />
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        gap={6}
      >
        <StyledButton data-testid="go-back" variant="tertiary" onClick={onBack}>
          {t("accounts.addManually.back")}
        </StyledButton>
        <StyledButton
          data-testid="create-account"
          variant="primary"
          isFetching={isPending}
          onClick={handleCreateAccount}
        >
          {t("accounts.addManually.create")}
        </StyledButton>
      </Box>
    </Box>
  );
};
